import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, setDoc } from "firebase/firestore";
import { database } from "../../database/firebase";
import { useSelector, useDispatch } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { initQuestions } from "../../functions/setUserQuestions";
import "./waiting.css";
import { setBackButtonUrl } from '../uiSlice';

const TOTAL_USER_COUNT_KEY = "mythFactTotalUserCount";
const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

const CHARACTER_SLOTS = ["c1", "c2", "c3", "c4", "c5"];

function sortLiveUsersForCharacterAssign(users) {
  return [...users].sort((a, b) => {
    const ta = Number(a?.timestamp ?? 0);
    const tb = Number(b?.timestamp ?? 0);
    if (ta !== tb) return ta - tb;
    return String(a?.userId ?? a?.id ?? "").localeCompare(
      String(b?.userId ?? b?.id ?? "")
    );
  });
}

function characterForLobbySlotIndex(slotIndex) {
  return CHARACTER_SLOTS[
    Math.min(Math.max(Number(slotIndex) || 0, 0), CHARACTER_SLOTS.length - 1)
  ];
}

function pickAvailableCharacter(users, fallbackIndex = 0) {
  const used = new Set(
    users
      .map((u) => normalizeCharacterName(u?.character))
      .filter(Boolean)
  );
  const next = CHARACTER_SLOTS.find((slot) => !used.has(slot));
  return next || characterForLobbySlotIndex(fallbackIndex);
}

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function normalizeTotalUserCount(value) {
  return Number(value) === 2 ? 2 : 4;
}

function resolveLobbyName(locationState, userData) {
  const fromState =
    typeof locationState?.lobbyName === "string" ? locationState.lobbyName.trim() : "";
  if (fromState) return fromState;
  try {
    const fromSession = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (fromSession && fromSession.trim()) return fromSession.trim();
  } catch {
    // ignore storage failures
  }
  return String(userData?.lobby ?? "").trim();
}

function stripNullWords(value) {
  return String(value ?? "")
    .replace(/\bnull\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getDisplayName(userData) {
  const name = stripNullWords(userData?.name);
  if (name) return name;
  const first = stripNullWords(userData?.firstName);
  if (first) return first;
  const last = stripNullWords(userData?.lastName);
  if (last) return last;
  return `${first} ${last}`.trim();
}

function normalizeCharacterName(character) {
  const value = String(character || "").trim().toLowerCase();
  return /^c[1-6]$/.test(value) ? value : "";
}

function isPresenterValue(value) {
  if (value === true) return true;
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
}

function getPlayerLabel(player, fallbackUserData, order) {
  const fromName = stripNullWords(player?.name);
  if (fromName) return fromName;

  const first = stripNullWords(player?.firstName ?? player?.first_name);
  const last = stripNullWords(player?.lastName ?? player?.last_name);
  const fullName = `${first} ${last}`.trim();
  if (fullName) return fullName;

  const fromEmail = String(player?.email ?? "").trim();
  if (fromEmail) return fromEmail;

  const fromEmpId = String(player?.emp_id ?? player?.employeeId ?? "").trim();
  if (fromEmpId) return fromEmpId;

  const fallbackName = stripNullWords(fallbackUserData?.name);
  if (fallbackName) return fallbackName;

  const fallbackEmail = String(fallbackUserData?.email ?? "").trim();
  if (fallbackEmail) return fallbackEmail;

  const fallbackEmpId = String(
    fallbackUserData?.emp_id ?? fallbackUserData?.employeeId ?? ""
  ).trim();
  if (fallbackEmpId) return fallbackEmpId;

  return `Player ${order}`;
}

export default function Waiting() {
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const themeData = useSelector((state) => state.theme?.data);
  const themeColors = themeData?.colors || themeData || {};
  const textStyle = themeColors?.text_color
    ? { color: themeColors.text_color }
    : undefined;
  const buttonStyle =
    themeColors?.option_color != null && themeColors?.option_text_color != null
      ? {
          backgroundColor: themeColors.option_color,
          color: themeColors.option_text_color,
        }
      : undefined;
  const themeCurrentName = useSelector((state) => state.theme?.currentTheme);
  const userData = useMemo(() => readStoredUserData(), []);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [isPresenter, setIsPresenter] = useState(() => isPresenterValue(userData?.presenter));
  const [questionsInitialized, setQuestionsInitialized] = useState(false);
  const [maxLobbyPlayers, setMaxLobbyPlayers] = useState(() => {
    try {
      const stored = sessionStorage.getItem(TOTAL_USER_COUNT_KEY);
      if (stored != null) return normalizeTotalUserCount(stored);
    } catch {
      // ignore storage failures
    }
    return normalizeTotalUserCount(userData?.totalUserCount);
  });

  const userId = String(userData?.userId ?? userData?.userid ?? userData?.id ?? "").trim();
  const sessionId = String(userData?.sessionId ?? "").trim();
  const lobbyName = useMemo(
    () => resolveLobbyName(location.state, userData),
    [location.state, userData]
  );
  const displayName = getDisplayName(userData);
  const { status, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();


  useEffect(() => {
    dispatch(setBackButtonUrl("lobby"));
  }, [status, user]);



  useEffect(() => {
    if (!lobbyName) return;
    try {
      sessionStorage.setItem(CURRENT_LOBBY_KEY, lobbyName);
    } catch {
      // ignore storage failures
    }
    if (String(userData?.lobby ?? "").trim() === lobbyName) return;
    try {
      const merged = { ...(userData || {}), lobby: lobbyName };
      const payload = JSON.stringify(merged);
      localStorage.setItem("userData", payload);
      sessionStorage.setItem("userData", payload);
    } catch {
      // ignore storage failures
    }
  }, [lobbyName, userData]);

  useEffect(() => {
    if (questionsInitialized) return;
    if (!userId || !sessionId || !lobbyName) return;

    const organizationId = String(userData?.organizationId ?? "").trim();
    const themeName = String(themeData?.themename ?? themeCurrentName ?? "").trim();
    if (!adminToken || !organizationId || !themeName) return;

    let cancelled = false;

    const initializeQuestions = async () => {
      try {
        await initQuestions({
          userId,
          sessionId,
          organizationId,
          email: userData?.email || "",
          fullName: displayName,
          themeName,
          token: adminToken,
          lobby: lobbyName,
          link: "",
          loginPin: "",
        });
        if (!cancelled) setQuestionsInitialized(true);
      } catch (e) {
        console.error("Failed to initialize questions on waiting page:", e);
      }
    };

    initializeQuestions();

    return () => {
      cancelled = true;
    };
  }, [
    adminToken,
    displayName,
    lobbyName,
    questionsInitialized,
    sessionId,
    themeCurrentName,
    themeData?.themename,
    userData?.email,
    userData?.organizationId,
    userId,
  ]);

  const handlePlayNow = async () => {
    if (starting) return;
    setError("");

    if (!isPresenter) {
      setError("Only presenter can start the game.");
      return;
    }

    if (players.length !== maxLobbyPlayers) {
      setError("Waiting for all players to join...");
      return;
    }

    if (!userId || !sessionId || !lobbyName) {
      setError("Missing lobby details. Please join the lobby again.");
      return;
    }

    try {
      setStarting(true);

      // 2) Create shared lobby/game state doc (idempotent).
      const gameDocRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);
      const existing = await getDoc(gameDocRef);
      const existingStatus = String(existing.data()?.status ?? "").trim().toLowerCase();

      if (existing.exists() && existingStatus === "play") {
        navigate("/jenga");
        return;
      }
      if (existing.exists() && existingStatus === "gameover") {
        navigate("/leaderboard");
        return;
      }

      const playPayload = {
        status: "play",
        time: "30",
        turn: "",
        totalUserCount: maxLobbyPlayers,
        questions: themeData?.questions || [],
        currentQuestion: 0,
      };

      await setDoc(
        gameDocRef,
        existing.exists()
          ? playPayload
          : {
              ...playPayload,
              createdAt: Date.now(),
              createdBy: userId,
            },
        { merge: true }
      );

      navigate("/jenga");
    } catch (e) {
      console.error("Play Now failed:", e);
      setError(String(e?.message || e || "Failed to start game."));
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (!userId || !sessionId || !lobbyName) {
      setError("Missing lobby details. Please join the lobby again.");
      setLoading(false);
      return;
    }

    let unsubscribeUsers = null;
    let unsubscribeGame = null;
    let isActive = true;
    const usersPath = `lobbies/${sessionId}/lobby/${lobbyName}/users`;
    const usersRef = collection(database, usersPath);
    const userDocRef = doc(database, `${usersPath}/${userId}`);
    const gameDocRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);

    const joinCurrentUser = async () => {
      try {
        if (!isActive) return;
        const existingGameDoc = await getDoc(gameDocRef);
        const existingGameStatus = String(
          existingGameDoc.data()?.status ?? ""
        ).trim().toLowerCase();
        if (existingGameStatus === "gameover") {
          navigate("/leaderboard");
          return;
        }
        const existingUserDoc = await getDoc(userDocRef);
        if (!existingUserDoc.exists()) {
          const presenter = isPresenterValue(userData?.presenter);
          const usersSnapshot = await getDocs(usersRef);
          const liveUsers = usersSnapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((p) => p?.status === "live");
          const assignedCharacter = pickAvailableCharacter(liveUsers, liveUsers.length);

          await setDoc(
            userDocRef,
            {
              userId,
              sessionId,
              lobby: lobbyName,
              organizationId: userData?.organizationId || "",
              name: displayName,
              email: userData?.email || "",
              emp_id: userData?.emp_id || userData?.employeeId || "",
              employeeId: userData?.employeeId || userData?.emp_id || "",
              timestamp: Date.now(),
              status: "live",
              presenter,
              character: assignedCharacter,
            },
            { merge: true }
          );
          if (isActive) setIsPresenter(presenter);
        } else {
          // Keep existing user fields, but mark this user as active on the waiting page.
          const existingPresenter = isPresenterValue(existingUserDoc.data()?.presenter);
          const existingCharacter = normalizeCharacterName(existingUserDoc.data()?.character);
          let patch = { status: "live" };

          if (!existingCharacter) {
            const usersSnapshot = await getDocs(usersRef);
            const liveUsers = usersSnapshot.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .filter((p) => String(p?.userId || "").trim() !== userId && p?.status === "live");
            patch = {
              ...patch,
              character: pickAvailableCharacter(liveUsers, liveUsers.length),
            };
          }

          await setDoc(userDocRef, patch, { merge: true });
          if (isActive) setIsPresenter(existingPresenter);
        }
      } catch (e) {
        if (isActive) {
          console.error("Failed to upsert current lobby user:", e);
        }
      }
    };

    joinCurrentUser();

    unsubscribeUsers = onSnapshot(
      query(usersRef, orderBy("timestamp", "asc")),
      (snap) => {
        if (!isActive) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const sortedLive = sortLiveUsersForCharacterAssign(
          list.filter((p) => p?.status === "live")
        );
        // Show only users currently on the waiting page.
        setPlayers(sortedLive.slice(0, maxLobbyPlayers));

        setLoading(false);
      },
      (e) => {
        if (!isActive) return;
        console.error("Waiting lobby snapshot failed:", e);
        setError("Could not load lobby players.");
        setLoading(false);
      }
    );

    // If shared lobby status changes, route everyone accordingly.
    unsubscribeGame = onSnapshot(
      gameDocRef,
      (snap) => {
        if (!isActive || !snap.exists()) return;
        const gameStatus = String(snap.data()?.status ?? "").trim().toLowerCase();
        const resolvedTotalUserCount = normalizeTotalUserCount(snap.data()?.totalUserCount);
        setMaxLobbyPlayers(resolvedTotalUserCount);
        try {
          sessionStorage.setItem(TOTAL_USER_COUNT_KEY, String(resolvedTotalUserCount));
        } catch {
          // ignore storage write failures
        }
        if (gameStatus === "play") {
          navigate("/jenga");
          return;
        }
        if (gameStatus === "gameover") {
          navigate("/leaderboard");
        }
      },
      (e) => {
        if (!isActive) return;
        console.error("Waiting game status snapshot failed:", e);
      }
    );

    return () => {
      isActive = false;
      if (unsubscribeUsers) unsubscribeUsers();
      if (unsubscribeGame) unsubscribeGame();
    };
  }, [displayName, lobbyName, navigate, sessionId, userData?.email, userData?.organizationId, userId, maxLobbyPlayers]);

  const slots = useMemo(() => {
    const filled = players.map((p, idx) => ({
      joined: true,
      label: getPlayerLabel(p, String(p?.userId || "") === userId ? userData : null, idx + 1),
      order: idx + 1,
      isMe: String(p?.userId || "") === userId,
      character: normalizeCharacterName(p?.character),
    }));
    while (filled.length < maxLobbyPlayers) {
      filled.push({
        joined: false,
        label: "......",
        order: filled.length + 1,
        isMe: false,
        character: "",
      });
    }
    return filled;
  }, [maxLobbyPlayers, players, userData, userId]);

  return (
    <div className="waiting-page">
      <div className="waiting-overlay" />
      <h1 className="waiting-title" style={textStyle}>
        Waiting for players..
      </h1>

      {loading ? (
        <p className="waiting-message" style={textStyle}>
          Loading lobby...
        </p>
      ) : null}
      {error ? (
        <p className="waiting-error" style={textStyle}>
          {error}
        </p>
      ) : null}

      <div
        className={`waiting-grid waiting-grid--${maxLobbyPlayers === 2 ? "two" : "four"}`}
      >
        {slots.map((slot, idx) => (
          <div
            key={`${slot.order}-${idx}`}
            className={`waiting-card ${slot.joined ? "joined" : "empty"} ${slot.isMe ? "me" : ""}`}
          >
            <div className="waiting-order">{slot.order}</div>
            <div className="waiting-avatar">
              {slot.joined && slot.character ? (
                <img
                  className="waiting-avatar-image"
                  src={`/img/${slot.character}.png`}
                  alt={slot.character}
                />
              ) : slot.joined ? (
                "🙂"
              ) : (
                "…"
              )}
            </div>
            <div className="waiting-name">{slot.label}</div>
          </div>
        ))}
      </div>

      {isPresenter ? (
        <button
          className="waiting-play-btn"
          type="button"
          disabled={players.length !== maxLobbyPlayers || starting}
          onClick={handlePlayNow}
          style={buttonStyle}
        >
          {starting ? "Starting..." : "Play Now"}
        </button>
      ) : null}
    </div>
  );
}

